import React, { useEffect, useRef, useState } from 'react';
import { Dialog } from '@/shared/components/Dialog';
import { ChevronDown } from 'react-feather';
import { Dropdown } from '@/shared/components/Dropdown';
import { Textarea } from '@/shared/components/TextField';
import {
  TECHNICIAN_MISSED_VISIT_REASONS,
  TECHNICIAN_UPDATE_SR_STATUS_LABELS as L,
  type TechnicianMissedVisitReasonId,
} from '../../constants/labels';
import type { ServiceRequestStatus } from '../../types/serviceRequestListType';
import {
  STATUS_CONFIG,
  formatScheduledDate,
  formatScheduledTime,
} from './viewServiceRequestHelpers';
import { SRStatusBadge } from './viewServiceRequestUI';

/** Read-only scheduled-visit details shown when reporting a missed visit. */
export interface TechnicianMissedVisitSummary {
  scheduledStartAt: string | null | undefined;
  scheduledEndAt: string | null | undefined;
  serviceTypeName: string | null | undefined;
  clientName: string | null | undefined;
  /** Pre-formatted facility location (address line(s), city, state, country). */
  location: string | null | undefined;
}

interface TechnicianUpdateStatusModalProps {
  open: boolean;
  onClose: () => void;
  currentStatus: ServiceRequestStatus;
  allowedStatuses: ServiceRequestStatus[];

  /** `status` (step 1) or `details` (step 2, the "Unfulfilled Visit" step). */
  step: 'status' | 'details';
  /** Step-1 primary action when the selected target is `unfulfilled`. */
  onContinue: () => void;

  newStatus: ServiceRequestStatus | '';
  onNewStatusChange: (status: ServiceRequestStatus) => void;
  newStatusError: string;

  notes: string;
  onNotesChange: (value: string) => void;
  notesError: string;

  // Missed-visit flow (RR-622) — only used when `newStatus === 'unfulfilled'`.
  missedVisitReason: TechnicianMissedVisitReasonId | '';
  onMissedVisitReasonChange: (id: TechnicianMissedVisitReasonId) => void;
  missedVisitReasonError: string;
  /** Read-only scheduled-visit details rendered before the comment fields. */
  summary: TechnicianMissedVisitSummary;

  isSubmitting: boolean;
  submitError: string;
  onSubmit: () => void;
  canSubmit: boolean;
}

const InfoIcon: React.FC = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="9" stroke="#474A4C" strokeWidth="1.5" />
    <line
      x1="12"
      y1="11"
      x2="12"
      y2="16.5"
      stroke="#474A4C"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <circle cx="12" cy="8" r="1" fill="#474A4C" />
  </svg>
);

// Matches the value styling used by the admin update-status modal's
// scheduled-visit card — a muted 12px label above a 16/500 value.
const ReadOnlyField: React.FC<{ label: string; value: string }> = ({
  label,
  value,
}) => (
  <div className="flex flex-col gap-1 min-w-0">
    <span
      style={{
        fontFamily: 'Roboto',
        fontSize: 12,
        lineHeight: '16px',
        fontWeight: 400,
        letterSpacing: 0.4,
        color: '#6a7278',
      }}
    >
      {label}
    </span>
    <span
      className="wrap-break-word"
      style={{
        fontFamily: 'Roboto',
        fontSize: 16,
        lineHeight: '24px',
        fontWeight: 500,
        color: '#474A4C',
      }}
    >
      {value || '—'}
    </span>
  </div>
);

const ScheduledVisitCard: React.FC<{
  summary: TechnicianMissedVisitSummary;
}> = ({ summary }) => {
  const fmtDate = (iso: string | null | undefined) => {
    const v = formatScheduledDate(iso);
    return v === '—' || v === '-' ? '' : v;
  };
  const fmtTime = (iso: string | null | undefined) => {
    const v = formatScheduledTime(iso);
    return v === '—' || v === '-' ? '' : v;
  };
  // Render the visit window as a "10:00 – 12:00" range, falling back to either
  // bound alone when the other is missing.
  const start = fmtTime(summary.scheduledStartAt);
  const end = fmtTime(summary.scheduledEndAt);
  const timeRange = start && end ? `${start} – ${end}` : start || end;
  return (
    <div
      className="rounded-lg border border-[#DCDDE0]"
      style={{ backgroundColor: '#FFFFFF', padding: 16 }}
      data-testid="technician-missed-visit-card"
    >
      <h3
        style={{
          fontFamily: 'Roboto',
          fontSize: 18,
          lineHeight: '24px',
          fontWeight: 500,
          letterSpacing: 0.1,
          color: '#474A4C',
          marginBottom: 16,
        }}
      >
        {L.MISSED_VISIT_SECTION}
      </h3>
      <div className="grid grid-cols-2 gap-x-6 gap-y-4">
        <ReadOnlyField
          label={L.FIELD_SCHEDULED_DATE}
          value={fmtDate(summary.scheduledStartAt)}
        />
        <ReadOnlyField label={L.FIELD_SCHEDULED_TIME} value={timeRange} />
        <ReadOnlyField
          label={L.FIELD_SERVICE_TYPE}
          value={summary.serviceTypeName ?? ''}
        />
        <ReadOnlyField
          label={L.FIELD_CLIENT}
          value={summary.clientName ?? ''}
        />
        <div className="col-span-2">
          <ReadOnlyField
            label={L.FIELD_LOCATION}
            value={summary.location ?? ''}
          />
        </div>
      </div>
    </div>
  );
};

/**
 * Technician "Update Task Status" modal (RR-624) — the single-step counterpart
 * to {@link UpdateStatusModal}. Renders a disabled current-status chip, the
 * (technician-filtered) new-status dropdown, the irreversibility banner, and
 * an optional notes field. No "Send email" checkbox and no cancel-reason step.
 *
 * Missed-visit flow (RR-622): when the selected target is `unfulfilled`,
 * clicking "Zmiana statusu" advances to a dedicated second step ("Unfulfilled
 * Visit") instead of submitting. That step shows a read-only scheduled-visit
 * card (date, time range, service type, client, location), a mandatory reason
 * dropdown, and an additional-comment field (required only when the reason is
 * "Other"), submitted via "Wyślij". Every other target submits straight from
 * step 1 with the optional notes field.
 */
const TechnicianUpdateStatusModal: React.FC<
  TechnicianUpdateStatusModalProps
> = ({
  open,
  onClose,
  currentStatus,
  allowedStatuses,
  step,
  onContinue,
  newStatus,
  onNewStatusChange,
  newStatusError,
  notes,
  onNotesChange,
  notesError,
  missedVisitReason,
  onMissedVisitReasonChange,
  missedVisitReasonError,
  summary,
  isSubmitting,
  submitError,
  onSubmit,
  canSubmit,
}) => {
  const hasTransitions = allowedStatuses.length > 0;
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isReasonDropdownOpen, setIsReasonDropdownOpen] = useState(false);
  // Refs used to keep the (downward-opening) reason menu fully in view: when it
  // opens we scroll the modal body so the trigger sits near the top, leaving
  // room below for every option instead of clipping the lower ones.
  const bodyRef = useRef<HTMLDivElement>(null);
  const reasonFieldRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) {
      setIsDropdownOpen(false);
      setIsReasonDropdownOpen(false);
    }
  }, [open]);

  useEffect(() => {
    if (!isReasonDropdownOpen) {
      return;
    }
    const body = bodyRef.current;
    const field = reasonFieldRef.current;
    if (!body || !field) {
      return;
    }
    // Wait a frame so the menu has rendered, then scroll the trigger up to the
    // top of the scrollable body (with a small gap) to maximise the space the
    // downward menu has to show all of its options.
    const raf = requestAnimationFrame(() => {
      const delta =
        field.getBoundingClientRect().top - body.getBoundingClientRect().top;
      body.scrollTo({ top: body.scrollTop + delta - 8, behavior: 'smooth' });
    });
    return () => cancelAnimationFrame(raf);
  }, [isReasonDropdownOpen]);
  const displayNewStatusError = isDropdownOpen ? '' : newStatusError;
  const displayMissedVisitReasonError = isReasonDropdownOpen
    ? ''
    : missedVisitReasonError;

  const isUnfulfilled = newStatus === 'unfulfilled';

  const reasonItems = TECHNICIAN_MISSED_VISIT_REASONS.map((r) => ({
    id: r.id,
    label: r.label,
    toolTip: false as const,
  }));
  const selectedReasonLabel =
    TECHNICIAN_MISSED_VISIT_REASONS.find((r) => r.id === missedVisitReason)
      ?.label ?? '';
  const isOtherReason = missedVisitReason === 'other';

  const items = allowedStatuses.map((status) => {
    const cfg = STATUS_CONFIG[status];
    const text = cfg?.text ?? '#9CA3AF';
    const bg = cfg?.bg ?? '#F3F4F6';
    const labelText = cfg?.label ?? status;
    return {
      id: status,
      label: '',
      toolTip: false as const,
      leadingIcon: (
        <span
          className="inline-flex items-center gap-2 rounded-lg"
          style={{
            backgroundColor: bg,
            color: text,
            paddingLeft: 8,
            paddingRight: 12,
            paddingTop: 6,
            paddingBottom: 6,
            fontSize: 14,
            lineHeight: '20px',
            letterSpacing: '0.1px',
            fontWeight: 500,
          }}
        >
          <span
            className="rounded-full shrink-0"
            style={{ width: 6, height: 6, backgroundColor: text }}
          />
          {labelText}
        </span>
      ),
    };
  });

  const selectedCfg = newStatus ? STATUS_CONFIG[newStatus] : undefined;

  // Step 2 ("Unfulfilled Visit") is reached only after the technician selects
  // the `unfulfilled` target on step 1 and clicks "Zmiana statusu".
  const isDetailsStep = step === 'details';

  const handlePrimary = () => {
    if (isDetailsStep) {
      onSubmit();
      return;
    }
    // Step 1: the `unfulfilled` target advances to the details step; every
    // other target submits straight away (the single-step RR-624 flow).
    if (isUnfulfilled) {
      onContinue();
    } else {
      onSubmit();
    }
  };

  const primaryLabel = isSubmitting
    ? L.BUTTON_SUBMITTING
    : isDetailsStep
      ? L.BUTTON_SEND
      : L.BUTTON_SUBMIT;
  const primaryDisabled = !canSubmit;

  const footer = (
    <div className="flex items-center justify-end gap-3 px-6 py-6 shrink-0">
      <button
        type="button"
        onClick={onClose}
        disabled={isSubmitting}
        className={`h-10 px-5 rounded-lg border border-[#DCDDE0] bg-white text-[#181A1B] text-sm font-medium leading-5 tracking-[0.1px] transition-colors ${
          isSubmitting
            ? 'opacity-50 cursor-not-allowed'
            : 'hover:bg-[#F3F4F6] cursor-pointer'
        }`}
        data-testid="technician-update-sr-status-cancel"
      >
        {L.BUTTON_CANCEL}
      </button>
      <button
        type="button"
        onClick={handlePrimary}
        disabled={primaryDisabled}
        className={`h-10 px-5 rounded-lg bg-[#2A4455] text-white text-sm font-medium leading-5 tracking-[0.1px] transition-colors ${
          primaryDisabled
            ? 'opacity-50 cursor-not-allowed'
            : 'hover:bg-[#1f3340] cursor-pointer'
        }`}
        data-testid="technician-update-sr-status-submit"
      >
        {primaryLabel}
      </button>
    </div>
  );

  const submitErrorBlock = submitError ? (
    <p
      className="text-error-40"
      style={{ fontSize: 12, lineHeight: '16px' }}
      data-testid="technician-update-sr-submit-error"
    >
      {submitError}
    </p>
  ) : null;

  // ── Step 2: the "Unfulfilled Visit" details modal ───────────────────────
  if (isDetailsStep) {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth={700}
        closeOnOverlayClick={!isSubmitting}
        closeOnEscape={!isSubmitting}
        data-testid="technician-update-sr-status-modal"
      >
        {/*
          Fixed 778px popup height with the body scrolling. Capped to the
          viewport (minus the dialog's 24px×2 margin) so it never overflows the
          screen on 1024×768 or at 150% zoom — there it shrinks and the body
          simply scrolls instead of the footer falling off-screen.
        */}
        <div
          className="flex flex-col relative"
          style={{ height: 778, maxHeight: 'calc(100vh - 48px)' }}
        >
          <div className="px-6 pt-6 pb-2 flex flex-col gap-2 shrink-0">
            <h2
              className="text-neutral-10 font-normal"
              style={{ fontSize: 22, lineHeight: '28px' }}
              data-testid="technician-update-sr-status-title"
            >
              {L.UNFULFILLED_TITLE}
            </h2>
            <p
              style={{
                fontSize: 14,
                lineHeight: '20px',
                color: '#6a7278',
              }}
              data-testid="technician-unfulfilled-subtitle"
            >
              {L.UNFULFILLED_SUBTITLE}
            </p>
          </div>

          <div
            ref={bodyRef}
            className="px-6 pt-4 pb-2 flex flex-col gap-6 flex-1 min-h-0 overflow-y-auto"
          >
            <ScheduledVisitCard summary={summary} />

            <div
              ref={reasonFieldRef}
              className="flex flex-col gap-2 w-[318px] max-w-full"
              data-field="technician-missed-visit-reason"
            >
              <Dropdown
                menuFullWidth
                toolTip={false}
                menuMaxVisibleItems={4}
                disabled={isSubmitting}
                open={isReasonDropdownOpen}
                onOpenChange={setIsReasonDropdownOpen}
                items={reasonItems}
                {...(missedVisitReason
                  ? { selectedId: missedVisitReason }
                  : {})}
                onItemClick={(id) =>
                  onMissedVisitReasonChange(id as TechnicianMissedVisitReasonId)
                }
                error={Boolean(displayMissedVisitReasonError)}
                errorMessage={displayMissedVisitReasonError}
                trigger={({ open: isOpen, toggle, disabled, error }) => {
                  const labelFloating = isOpen || !!missedVisitReason;
                  const labelColor = isOpen ? '#2a4455' : '#6a7278';
                  return (
                    <button
                      type="button"
                      onClick={disabled ? undefined : toggle}
                      aria-expanded={isOpen}
                      aria-haspopup="true"
                      aria-disabled={disabled}
                      data-testid="technician-missed-visit-reason-button"
                      className={`w-full flex items-center justify-between gap-2 bg-transparent outline-none text-left ${
                        disabled
                          ? 'cursor-not-allowed text-neutral-60'
                          : 'cursor-pointer text-neutral-10'
                      }`}
                    >
                      <div
                        className="flex-1 min-w-0 relative flex flex-col justify-center"
                        style={{ minHeight: 40 }}
                      >
                        <span
                          className="absolute left-0 pointer-events-none origin-top-left font-normal"
                          style={{
                            color: labelColor,
                            top: labelFloating ? 0 : 10,
                            fontSize: labelFloating ? 12 : 16,
                            lineHeight: labelFloating ? '16px' : '20px',
                            transition:
                              'top 200ms ease-out, font-size 200ms ease-out, color 200ms ease-out',
                          }}
                        >
                          {L.FIELD_MISSED_VISIT_REASON}
                        </span>
                        {selectedReasonLabel && (
                          <span
                            className="self-start"
                            style={{
                              marginTop: 16,
                              fontSize: 14,
                              lineHeight: '20px',
                              fontWeight: 400,
                              color: '#181A1B',
                            }}
                          >
                            {selectedReasonLabel}
                          </span>
                        )}
                      </div>
                      <ChevronDown
                        strokeWidth={1.5}
                        className={`shrink-0 transition-transform duration-200 ${
                          isOpen ? 'rotate-180' : ''
                        } ${error ? 'text-error-40' : ''}`}
                      />
                    </button>
                  );
                }}
              />
            </div>

            <div data-field="technician-missed-visit-comment">
              <Textarea
                label={
                  <>
                    {L.FIELD_MISSED_VISIT_COMMENT}
                    {!isOtherReason && (
                      <>
                        {' '}
                        <span style={{ fontSize: 12 }}>
                          {L.FIELD_MISSED_VISIT_COMMENT_OPTIONAL}
                        </span>
                      </>
                    )}
                  </>
                }
                placeholder={L.PLACEHOLDER_MISSED_VISIT_COMMENT}
                value={notes}
                onChange={(e) =>
                  onNotesChange((e.target as HTMLTextAreaElement).value ?? '')
                }
                maxLength={L.NOTES_MAX_LENGTH}
                rows={4}
                fullWidth
                error={Boolean(notesError)}
                errorMessage={notesError}
                disabled={isSubmitting}
                dataTestId="technician-missed-visit-comment-input"
              />
            </div>

            {submitErrorBlock}
          </div>

          {footer}
        </div>
      </Dialog>
    );
  }

  // ── Step 1: the status-selection modal ──────────────────────────────────
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={700}
      closeOnOverlayClick={!isSubmitting}
      closeOnEscape={!isSubmitting}
      data-testid="technician-update-sr-status-modal"
    >
      <div className="flex flex-col relative">
        <div className="px-6 pt-6 pb-2 flex flex-col gap-4">
          <h2
            className="text-neutral-10 font-normal"
            style={{ fontSize: 22, lineHeight: '28px' }}
            data-testid="technician-update-sr-status-title"
          >
            {L.TITLE}
          </h2>
        </div>

        <div className="px-6 pt-4 flex flex-col gap-6">
          <div
            className="relative rounded-lg bg-[#E5E7E9] opacity-60 cursor-not-allowed px-3 w-[318px] max-w-full"
            style={{ minHeight: 56 }}
            data-testid="technician-update-sr-status-current-field"
            aria-disabled="true"
          >
            <span
              className="absolute left-3 pointer-events-none"
              style={{
                top: 0,
                paddingTop: 8,
                fontSize: 12,
                lineHeight: '16px',
                color: '#6a7278',
              }}
            >
              {L.FIELD_CURRENT_STATUS}
            </span>
            <div
              className="pt-7 pb-2"
              data-testid="technician-update-sr-current-status-value"
            >
              <SRStatusBadge status={currentStatus} />
            </div>
            <div
              className="absolute bottom-0"
              style={{
                left: 6,
                right: 6,
                height: 1,
                backgroundColor: '#6a7278',
              }}
            />
          </div>

          <div className="flex flex-col gap-2">
            <div
              className="w-[318px] max-w-full"
              data-field="technician-update-sr-new-status"
            >
              <Dropdown
                menuFullWidth
                toolTip={false}
                disabled={!hasTransitions || isSubmitting}
                open={isDropdownOpen}
                onOpenChange={setIsDropdownOpen}
                items={
                  hasTransitions
                    ? items
                    : [
                        {
                          id: '__none__',
                          label: L.EMPTY_NO_OPTIONS,
                          disabled: true,
                        },
                      ]
                }
                {...(newStatus ? { selectedId: newStatus } : {})}
                onItemClick={(id) => {
                  if (allowedStatuses.includes(id as ServiceRequestStatus)) {
                    onNewStatusChange(id as ServiceRequestStatus);
                  }
                }}
                error={Boolean(displayNewStatusError)}
                errorMessage={displayNewStatusError}
                {...(!displayNewStatusError && !hasTransitions
                  ? { supportingText: L.HELPER_NO_TRANSITIONS }
                  : {})}
                trigger={({ open: isOpen, toggle, disabled, error }) => {
                  const labelFloating = isOpen || !!selectedCfg;
                  const labelColor = isOpen ? '#2a4455' : '#6a7278';
                  return (
                    <button
                      type="button"
                      onClick={disabled ? undefined : toggle}
                      aria-expanded={isOpen}
                      aria-haspopup="true"
                      aria-disabled={disabled}
                      data-testid="technician-update-sr-new-status-button"
                      className={`w-full flex items-center justify-between gap-2 bg-transparent outline-none text-left ${
                        disabled
                          ? 'cursor-not-allowed text-neutral-60'
                          : 'cursor-pointer text-neutral-10'
                      }`}
                    >
                      <div
                        className="flex-1 min-w-0 relative flex flex-col justify-center"
                        style={{ minHeight: 40 }}
                      >
                        <span
                          className="absolute left-0 pointer-events-none origin-top-left font-normal"
                          style={{
                            color: labelColor,
                            top: labelFloating ? 0 : 10,
                            fontSize: labelFloating ? 12 : 16,
                            lineHeight: labelFloating ? '16px' : '20px',
                            transition:
                              'top 200ms ease-out, font-size 200ms ease-out, color 200ms ease-out',
                          }}
                        >
                          {L.FIELD_NEW_STATUS}
                        </span>
                        {selectedCfg && (
                          <span
                            className="inline-flex items-center gap-2 rounded-lg self-start"
                            style={{
                              backgroundColor: selectedCfg.bg,
                              color: selectedCfg.text,
                              paddingLeft: 8,
                              paddingRight: 12,
                              paddingTop: 4,
                              paddingBottom: 4,
                              marginTop: 16,
                              fontSize: 14,
                              lineHeight: '20px',
                              letterSpacing: '0.1px',
                              fontWeight: 500,
                            }}
                          >
                            <span
                              className="rounded-full shrink-0"
                              style={{
                                width: 6,
                                height: 6,
                                backgroundColor: selectedCfg.text,
                              }}
                            />
                            {selectedCfg.label}
                          </span>
                        )}
                      </div>
                      <ChevronDown
                        strokeWidth={1.5}
                        className={`shrink-0 transition-transform duration-200 ${
                          isOpen ? 'rotate-180' : ''
                        } ${error ? 'text-error-40' : ''}`}
                      />
                    </button>
                  );
                }}
              />
            </div>

            <div
              className="flex items-start gap-2"
              data-testid="technician-update-sr-status-irreversible-banner"
            >
              <InfoIcon />
              <p
                style={{
                  fontSize: 14,
                  lineHeight: '20px',
                  color: '#474A4C',
                }}
              >
                <span className="font-semibold">
                  {L.BANNER_IRREVERSIBLE_BOLD}
                </span>
                {L.BANNER_IRREVERSIBLE_REST}
              </p>
            </div>
          </div>

          {/*
            Step 1 keeps the optional "Uwaga" notes field for every target
            except `unfulfilled` — that target collects its comment on the
            dedicated "Unfulfilled Visit" step instead.
          */}
          {!isUnfulfilled && (
            <div data-field="technician-update-sr-notes">
              <Textarea
                label={
                  <>
                    {L.FIELD_NOTES}{' '}
                    <span style={{ fontSize: 12 }}>
                      {L.FIELD_NOTES_OPTIONAL}
                    </span>
                  </>
                }
                placeholder={L.PLACEHOLDER_NOTES}
                value={notes}
                onChange={(e) =>
                  onNotesChange((e.target as HTMLTextAreaElement).value ?? '')
                }
                maxLength={L.NOTES_MAX_LENGTH}
                rows={4}
                fullWidth
                error={Boolean(notesError)}
                errorMessage={notesError}
                disabled={isSubmitting}
                dataTestId="technician-update-sr-notes-input"
              />
            </div>
          )}

          {submitErrorBlock}
        </div>

        {footer}
      </div>
    </Dialog>
  );
};

export default TechnicianUpdateStatusModal;
